import { supabase } from "@/integrations/supabase/client";

/**
 * Gets the final HTML for a document, using the correct priority:
 * 1. custom_rendered_content (if exists)
 * 2. custom_content (for custom documents)
 * 3. Compile template with merge values (preserves signature block!)
 * 4. Fallback to sections (rarely used)
 */
export async function getDocumentHtml(documentId: string): Promise<string> {
  // Fetch the document with all related data
  const { data: document, error: docError } = await supabase
    .from('generated_documents')
    .select(`
      *,
      document_template_library(template_content),
      document_templates(template_content)
    `)
    .eq('id', documentId)
    .single();

  if (docError || !document) {
    throw new Error('Document not found');
  }

  // Priority 1: Use custom_rendered_content if it exists
  if (document.custom_rendered_content) {
    console.log('🔵 [getDocumentHtml] Using custom_rendered_content');
    return document.custom_rendered_content;
  }

  // Priority 2: Custom documents use custom_content
  if (document.is_custom && document.custom_content) {
    console.log('🔵 [getDocumentHtml] Using custom_content (is_custom=true)');
    return document.custom_content;
  }

  // Priority 3: Compile template with merge values (THIS is what preserves signature block!)
  const templateId = document.template_library_id || document.template_id;
  if (templateId) {
    console.log('🔵 [getDocumentHtml] Compiling template with merge values');
    
    // Get template content
    const isLibraryTemplate = !!document.template_library_id;
    const templateTable = isLibraryTemplate 
      ? 'document_template_library' 
      : 'document_templates';
    
    const { data: template } = await supabase
      .from(templateTable)
      .select('template_content')
      .eq('id', templateId)
      .order('version', { ascending: false })
      .limit(1)
      .single();
    
    if (template?.template_content) {
      // Get merge values
      const mergeValues = document.merge_data || {};
      
      // Compile with Handlebars
      const Handlebars = (await import('handlebars')).default;
      const compiled = Handlebars.compile(template.template_content);
      const html = compiled(mergeValues);
      
      console.log('🔵 [getDocumentHtml] Template compiled successfully');
      console.log('🔵 [getDocumentHtml] Has signature-block?', html.includes('signature-block'));
      
      // CRITICAL FIX: Template returns a FULL HTML document with <html><head><style>...
      // But generatePdfAndUpload wraps content in its own <html> wrapper.
      // We need to extract just the BODY content to avoid nested HTML!
      
      if (html.includes('<!DOCTYPE') || html.includes('<html')) {
        console.log('🔵 [getDocumentHtml] Extracting body content from full HTML document');
        
        // Parse the HTML and extract just the body content
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const bodyContent = doc.body.innerHTML;
        
        console.log('🔵 [getDocumentHtml] Body content extracted, length:', bodyContent.length);
        console.log('🔵 [getDocumentHtml] Body still has signature-block?', bodyContent.includes('signature-block'));
        
        return bodyContent;
      }
      
      return html;
    }
  }

  // Priority 4: Fallback to sections (should rarely happen)
  console.log('🔵 [getDocumentHtml] Falling back to sections');
  const { data: sections } = await supabase
    .from('document_sections')
    .select('*')
    .eq('document_id', documentId)
    .order('section_number');

  if (sections && sections.length > 0) {
    let html = '<div>';
    html += `<h1>${document.document_name}</h1>`;
    sections.forEach((section) => {
      html += `<h2>${section.section_title}</h2>`;
      html += `<div>${section.section_content || '<p>(No content)</p>'}</div>`;
    });
    html += '</div>';
    return html;
  }

  // Final fallback
  return `<h1>${document.document_name}</h1><p>(No content available)</p>`;
}

/**
 * Gets the COMPLETE HTML document including DOCTYPE, head, styles, and body.
 * Used for PDF generation where the API needs the full document structure.
 * Unlike getDocumentHtml(), this does NOT extract just the body content.
 */
export async function getFullDocumentHtml(documentId: string): Promise<string> {
  const { data: document, error: docError } = await supabase
    .from('generated_documents')
    .select(`
      *,
      document_template_library(template_content),
      document_templates(template_content)
    `)
    .eq('id', documentId)
    .single();

  if (docError || !document) {
    throw new Error('Document not found');
  }

  // Priority 1: custom_rendered_content (already complete HTML)
  if (document.custom_rendered_content) {
    console.log('🔵 [getFullDocumentHtml] Using custom_rendered_content');
    return document.custom_rendered_content;
  }

  // Priority 2: custom_content (already complete HTML)
  if (document.is_custom && document.custom_content) {
    console.log('🔵 [getFullDocumentHtml] Using custom_content');
    return document.custom_content;
  }

  // Priority 3: Compile template - return FULL HTML, not just body
  const templateId = document.template_library_id || document.template_id;
  if (templateId) {
    console.log('🔵 [getFullDocumentHtml] Compiling full template document');
    
    const isLibraryTemplate = !!document.template_library_id;
    const templateTable = isLibraryTemplate 
      ? 'document_template_library' 
      : 'document_templates';
    
    const { data: template } = await supabase
      .from(templateTable)
      .select('template_content')
      .eq('id', templateId)
      .single();
    
    if (template?.template_content) {
      const mergeValues = document.merge_data || {};
      const Handlebars = (await import('handlebars')).default;
      
      // Register Handlebars helpers (same as generate-document edge function)
      Handlebars.registerHelper('eq', function(a: any, b: any) {
        return a === b;
      });

      Handlebars.registerHelper('or', function(a: any, b: any) {
        return a || b;
      });

      Handlebars.registerHelper('and', function(a: any, b: any) {
        return a && b;
      });
      
      const compiled = Handlebars.compile(template.template_content);
      const fullHtml = compiled(mergeValues);
      
      console.log('✅ [getFullDocumentHtml] Returning complete HTML document for PDF');
      console.log('✅ [getFullDocumentHtml] Has DOCTYPE?', fullHtml.includes('<!DOCTYPE'));
      console.log('✅ [getFullDocumentHtml] Has <style>?', fullHtml.includes('<style>'));
      
      return fullHtml; // Return the FULL compiled template
    }
  }

  // Fallback: build a complete HTML document
  console.log('🔵 [getFullDocumentHtml] Using fallback complete HTML');
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body { font-family: Cambria, Georgia, serif; font-size: 12pt; line-height: 1.5; margin: 1in; }
    h1 { font-size: 18pt; margin-bottom: 1em; }
  </style>
</head>
<body>
  <h1>${document.document_name}</h1>
  <p>(No content available)</p>
</body>
</html>`;
}
